<script setup lang="ts">
// ナビゲーションアイテムの型定義
interface NavItem {
	label: string;
	href: string;
}

const props = defineProps<{
	variant?: "default" | "footer";
}>();

// イベントの定義
const emit = defineEmits<{
	linkClick: [];
}>();

// ナビゲーションアイテムの定義
const navItems: NavItem[] = [
	{ label: "お知らせ", href: "/info" },
	{ label: "会社概要", href: "/company" },
	{ label: "電子公告", href: "/ir" },
	{ label: "お問い合わせ", href: "/contact" },
];

// 試算表ページ判定
const route = useRoute();
const isCompare = computed(() => {
	//   return route.name === 'compare'
	return route.path.includes("/fire/compare"); // /fire/compare/ パスを含む場合に試算表ページと判定
});

const filteredNavItems = computed(() => {
	if (isCompare.value) {
		// 試算表ページでは「お問い合わせ」のみ表示
		return navItems.filter((item) => item.label === "お問い合わせ");
	}
	return navItems;
});

// リンククリック時の処理
const handleLinkClick = () => {
	emit("linkClick");
};
</script>

<template>
  <nav :class="['nav-list', `nav-list_${props.variant ?? 'default'}`]">
    <ul class="list_items">
      <span class="list_line"></span>
      <li v-for="item in filteredNavItems" :key="item.label" class="list_item">
        <NuxtLink :to="item.href" class="list_link" @click="handleLinkClick" :target="isCompare ? '_blank' : undefined">
          <span class="list_label">{{ item.label }}</span>
          <span class="list_icon-wrap">
            <Icons name="chevron-right" class="chevron-right_icon"/>
          </span>
        </NuxtLink>
        <span class="list_line"></span>
      </li>
    </ul>
  </nav>
</template>

<style scoped lang="scss">
// モバイル用のスタイル
.nav-list {
  width: 100%;
}

.list_items {
  list-style: none;
  margin: 1.5rem auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.list_item {
  width: 100%;
  margin: 0;
  padding: 0;
  position: relative;

  // 下線と間隔のための余白
  &:not(:last-child) {
    margin-bottom: 0;
  }
}

.list_link {
  display: flex;
  align-items: center;
  justify-content: flex-start; // 左寄せ
  width: 100%;
  padding: 0.8rem 0;
  text-decoration: none;
  color: inherit;
  font-weight: 700;
  position: relative;
}

.list_label {
  flex: 1 1 auto;
  text-align: left;
  // 文字色はデフォルト
}

.list_icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  margin-left: 0.5rem;
}

// アイコン
.chevron-right_icon {
  align-self: flex-end;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem; // 24px
  height: 1.5rem; // 24px
  border: 0.063rem solid #2174C0; // 1px
  border-radius: 50%;
}

// 右矢印svgのサイズ調整
.svg-manager {
  :deep(svg) {
    width: 80%;
    height: 80%;
  }
}

.list_line {
  height: 0.0625rem;
}

// 常時表示の下線（グラデーション）
.list_line {
  display: block;
  width: 100%;
  background: linear-gradient(90deg, #2174C0 0%, #059696 100%);
  margin: 0;
  padding: 0;
}

// PC用のスタイル
@include mixin.media(pc) {
  .list_items {
    flex-direction: row;
    justify-content: flex-end;
    gap: 2.5rem;
    margin: 0 auto;
  }

  .list_item {
    width: auto;
    padding: 0;
    margin: 0;
    position: relative;
  }

  .list_link {
    padding: 0;
    font-size: 1rem;
    justify-content: center;
    align-items: center;
  }

  .list_label {
    text-align: center;
  }

  .list_icon-wrap {
    display: none; // PCではアイコン非表示
  }

  .list_line {
    display: none; // PCでは常時下線非表示
  }

  // PC用ホバー時のグラデーション下線
  .list_link {
    position: relative;

    &:hover {
      background: linear-gradient(90deg, #2174C0 0%, #059696 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: -0.125rem;
      left: 0;
      width: 100%;
      height: 0.125rem;
      background: transparent;
      transition: all 0.3s ease;
    }

    &:hover::after {
      background: linear-gradient(90deg, #2174C0 0%, #059696 100%);
    }
  }
}

// ------- フッター用 --------
.nav-list_footer {
  .list_label {
    font-weight: 400;
  }

  // PC用のスタイル
  @include mixin.media(pc) {
    .list_items {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
      margin: auto auto;
    }

    .list_label {
      font-size: 0.875rem;
      font-weight: 700;
    }
  }
}
</style>
